import type { ClawContent, StoryAsset, StoryGlobalConfig, StoryPayload, StoryShot } from '../types';

/**
 * 后端把分镜结构存成 Content.type === 'yolo_segment' 的一条消息内容，
 * 其中 content 是 JSON 字符串，schema 与 unidu 端 yoloSegmentMessage 对齐：
 * {
 *   aspect_ratio: string,
 *   resolution: string,
 *   model_version?: string,
 *   segments: [{
 *     index: number,
 *     prompt: string,
 *     duration: string,           // 单位秒，字符串
 *     task_type: string,
 *     material_paths: string[],   // 总材料引用（前端展示资产用）
 *     image_paths: string[],
 *     audio_path: string,
 *     video_path: string,
 *   }]
 * }
 */
export const YOLO_SEGMENT_CONTENT_TYPE = 'yolo_segment';

interface RawYoloStoryboard {
  index?: number;
  prompt?: string;
  duration?: string;
  task_type?: string;
  material_paths?: string[];
  image_paths?: string[];
  audio_path?: string;
  video_path?: string;
}

interface RawYoloSegmentMessage {
  aspect_ratio?: string;
  resolution?: string;
  model_version?: string;
  segments?: RawYoloStoryboard[];
}

const DEFAULT_GLOBAL_CONFIG: StoryGlobalConfig = {
  resolution: '1080p',
  aspectRatio: '16:9',
};

const inferAssetType = (path: string): StoryAsset['type'] => {
  const ext = path.split('.').pop()?.toLowerCase() ?? '';
  if (['png', 'jpg', 'jpeg', 'webp', 'gif', 'bmp'].includes(ext)) return 'image';
  if (['mp4', 'mov', 'webm', 'mkv'].includes(ext)) return 'video';
  if (['mp3', 'wav', 'm4a', 'aac', 'flac', 'ogg'].includes(ext)) return 'audio';
  return 'file';
};

const buildAsset = (path: string, explicitType?: StoryAsset['type']): StoryAsset => {
  const trimmed = path.trim();
  const name = trimmed.split('/').filter(Boolean).pop() ?? trimmed;
  return { path: trimmed, name, type: explicitType ?? inferAssetType(trimmed) };
};

const dedupeAssets = (
  paths: Array<string | undefined | null>,
  typeByPath: Map<string, StoryAsset['type']> = new Map(),
): StoryAsset[] => {
  const seen = new Set<string>();
  const out: StoryAsset[] = [];
  for (const raw of paths) {
    if (!raw) continue;
    const trimmed = raw.trim();
    if (!trimmed || seen.has(trimmed)) continue;
    seen.add(trimmed);
    out.push(buildAsset(trimmed, typeByPath.get(trimmed)));
  }
  return out;
};

const collectTypedMaterialAssets = (shot: RawYoloStoryboard): StoryAsset[] => {
  const typeByPath = new Map<string, StoryAsset['type']>();
  const explicitPaths: string[] = [];

  for (const rawPath of shot.image_paths ?? []) {
    const path = rawPath?.trim();
    if (!path) continue;
    typeByPath.set(path, 'image');
    explicitPaths.push(path);
  }

  const audioPath = shot.audio_path?.trim();
  if (audioPath) {
    typeByPath.set(audioPath, 'audio');
    explicitPaths.push(audioPath);
  }

  const videoPath = shot.video_path?.trim();
  if (videoPath) {
    typeByPath.set(videoPath, 'video');
    explicitPaths.push(videoPath);
  }

  return dedupeAssets([...(shot.material_paths ?? []), ...explicitPaths], typeByPath);
};

const parseDurationSec = (raw: unknown): number => {
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  if (typeof raw === 'string') {
    const n = Number.parseFloat(raw);
    if (Number.isFinite(n)) return n;
  }
  return 5;
};

const collectMentionedAssetPaths = (prompt: string, assets: StoryAsset[]): Set<string> => {
  const hits = new Set<string>();
  if (!prompt || !assets.length) return hits;
  // 用最长名优先匹配，避免"安宁-前世.png"被"安宁"前缀提前吃掉。
  const sorted = [...assets]
    .filter((asset) => !!asset.path && !!asset.name)
    .sort((a, b) => b.name.length - a.name.length);
  let index = 0;
  while (index < prompt.length) {
    if (prompt[index] === '@') {
      const sliced = prompt.slice(index + 1);
      let matched: StoryAsset | undefined;
      let matchedLength = 0;
      for (const asset of sorted) {
        // 后端 prompt 里 @ 引用既可能是 asset.name（文件名），也可能是完整的
        // 媒体路径 asset.path（如 `@media/sess-xxx/foo.png`），都按最长匹配命中。
        const candidates = [asset.path, asset.name, asset.path.split('/').filter(Boolean).pop() || ''];
        for (const candidate of candidates) {
          if (!candidate) continue;
          if (sliced.startsWith(candidate) && candidate.length > matchedLength) {
            matched = asset;
            matchedLength = candidate.length;
          }
        }
      }
      if (matched) {
        hits.add(matched.path);
        index += 1 + matchedLength;
        continue;
      }
    }
    index += 1;
  }
  return hits;
};

/**
 * detail 面板"引用资源"区默认展示后端 material_paths 全量。
 * 如果某个资源在 prompt 里没有 @ 引用，就在 prompt 末尾追加 `@{name}`，保证
 * 资源区与 prompt 里的 @ 列表始终同步，避免出现资源在但 prompt 没有提及的情况。
 */
const ensurePromptMentions = (prompt: string, assets: StoryAsset[]): { prompt: string; assets: StoryAsset[] } => {
  if (!assets.length) return { prompt, assets };
  const hits = collectMentionedAssetPaths(prompt, assets);
  const missing = assets.filter((asset) => !hits.has(asset.path));
  if (!missing.length) return { prompt, assets };
  const append = missing.map((asset) => `@${asset.name}`).join(' ');
  const trailing = !prompt || /\s$/.test(prompt) ? '' : ' ';
  return { prompt: `${prompt}${trailing}${append}`, assets };
};

const normalizeShot = (shot: RawYoloStoryboard, idx: number): StoryShot => {
  // material_paths 是后端"该分镜全部素材"的并集（image / audio / video 都包含在内）。
  // 类型语义以 image_paths / audio_path / video_path 为准，material_paths 只负责全量展示和排序兜底。
  // detail 面板的"引用资源"区默认展示 material_paths 全量；如果 prompt 里没有
  // 显式 @ 引用某个素材，由 ensurePromptMentions 在 prompt 末尾补齐 `@{name}`，
  // 让"引用资源"和 prompt 中的 @ 列表保持双向一致。
  // audio_path / video_path 当前 detail 面板不参与编辑，先存到 _raw 出站时回写。
  const materialAssets = collectTypedMaterialAssets(shot);
  const promptText = shot.prompt ?? '';
  const aligned = ensurePromptMentions(promptText, materialAssets);
  return {
    id: `shot-${shot.index ?? idx + 1}`,
    prompt: aligned.prompt,
    durationSec: parseDurationSec(shot.duration),
    assets: aligned.assets,
    _raw: {
      audioPath: shot.audio_path ?? '',
      videoPath: shot.video_path ?? '',
    },
  };
};

/**
 * 把 yolo_segment content 字段（JSON 字符串）解析成前端 StoryPayload。
 * 解析失败返回 null，调用方按"未识别为分镜"处理。
 */
export const parseYoloSegmentPayload = (
  raw: string | undefined | null,
  options: { mdFileName?: string } = {},
): StoryPayload | null => {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as RawYoloSegmentMessage;
    if (!parsed || !Array.isArray(parsed.segments)) return null;
    const shots = parsed.segments.map(normalizeShot);
    if (!shots.length) return null;
    return {
      mdFileName: options.mdFileName,
      shots,
      globalConfig: {
        resolution: parsed.resolution?.trim() || DEFAULT_GLOBAL_CONFIG.resolution,
        aspectRatio: parsed.aspect_ratio?.trim() || DEFAULT_GLOBAL_CONFIG.aspectRatio,
        modelVersion: parsed.model_version?.trim() || undefined,
      },
    };
  } catch {
    return null;
  }
};

const collectImagePaths = (assets: StoryAsset[]): string[] => {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const asset of assets) {
    const path = asset.path?.trim();
    if (!path || seen.has(path)) continue;
    const type = asset.type ?? inferAssetType(path);
    if (type !== 'image') continue;
    seen.add(path);
    out.push(path);
  }
  return out;
};

/**
 * 将前端编辑后的 StoryPayload 序列化回 yolo_segment content，
 * 用于 UpdateMessage 把整条消息的内容覆盖回后端。
 *
 * - image_paths 由当前 assets 中类型为 image 的引用维护（与 prompt 中的 @ 双向同步）。
 * - audio_path / video_path 当前 detail 面板不参与编辑，沿用入站时保存的 _raw 原值。
 * - material_paths 是后端"该分镜全部素材"的并集；前端展示的 assets 已对齐
 *   入站的 material_paths（且新增/删除的 @ 都会同步进 assets），
 *   出站时直接用 assets 的全部路径加 audio/video 拼回去。
 */
export const buildYoloSegmentContent = (story: StoryPayload): ClawContent => {
  const segments = story.shots.map((shot, idx) => {
    const imagePaths = collectImagePaths(shot.assets);
    const audioPath = shot._raw?.audioPath ?? '';
    const videoPath = shot._raw?.videoPath ?? '';
    const materialSeen = new Set<string>();
    const materialPaths: string[] = [];
    const assetPaths = shot.assets.map((asset) => asset.path?.trim()).filter((path): path is string => !!path);
    for (const candidate of [...assetPaths, audioPath, videoPath]) {
      if (!candidate || materialSeen.has(candidate)) continue;
      materialSeen.add(candidate);
      materialPaths.push(candidate);
    }
    return {
      index: idx + 1,
      prompt: shot.prompt,
      duration: String(shot.durationSec ?? 0),
      task_type: '',
      material_paths: materialPaths,
      image_paths: imagePaths,
      audio_path: audioPath,
      video_path: videoPath,
    };
  });
  const payload = {
    aspect_ratio: story.globalConfig.aspectRatio,
    resolution: story.globalConfig.resolution,
    ...(story.globalConfig.modelVersion ? { model_version: story.globalConfig.modelVersion } : {}),
    segments,
  };
  return {
    type: YOLO_SEGMENT_CONTENT_TYPE,
    content: JSON.stringify(payload),
  };
};
